import React, { useEffect, useState } from 'react';
import { X, AlertTriangle, CloudRain, MapPin, Calendar, Ban } from 'lucide-react';
import { formatAnnouncementExpiry, isAnnouncementCurrent } from '../lib/announcementExpiry';
import { AnnouncementMediaGallery } from './AnnouncementMediaGallery';
import { PopcornTeaser } from './PopcornCountdownCard';
import { KickoffCountdown } from './KickoffCountdown';
import { PlayoffHypeCard, PLAYOFF_ANNOUNCEMENT_ID } from './PlayoffHypeCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'bjp_dismissed_announcements';

const TYPE_META = {
  practice_cancellation: { label: 'Practice Cancelled', icon: Ban, color: '#c8102e' },
  weather_cancellation: { label: 'Weather Cancellation', icon: CloudRain, color: '#c8102e' },
  game_cancellation: { label: 'Game Cancelled', icon: Ban, color: '#c8102e' },
  venue_change: { label: 'Venue Change', icon: MapPin, color: '#d97706' },
  upcoming_event: { label: 'Upcoming Event', icon: Calendar, color: '#0a1f44' }
};

function getDismissed() {
  try { return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]')); } catch { return new Set(); }
}
function saveDismissed(set) {
  localStorage.setItem(DISMISS_KEY, JSON.stringify(Array.from(set)));
}

export default function AnnouncementPopup() {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(getDismissed());
  const [now, setNow] = useState(() => new Date());
  const visible = items.filter((item) => isAnnouncementCurrent(item, now) && !dismissed.has(item.id));

  useEffect(() => {
    if (visible.length === 0) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [visible.length]);

  useEffect(() => {
    fetch(`${API}/announcements`).then((r) => r.ok ? r.json() : []).then(setItems).catch(() => {});
  }, []);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 15 * 1000);
    return () => window.clearInterval(timer);
  }, []);

  if (visible.length === 0) return null;

  const dismiss = (id) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    saveDismissed(next);
  };

  const dismissAll = () => {
    const next = new Set(dismissed);
    visible.forEach((v) => next.add(v.id));
    setDismissed(next);
    saveDismissed(next);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-[#0a1f44]/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in" data-testid="announcement-popup" role="dialog" aria-modal="true" aria-labelledby="announcement-popup-title">
      <div className="bg-white w-full max-w-lg max-h-[92dvh] sm:max-h-[90vh] overflow-y-auto overscroll-contain shadow-2xl border-t-4 border-[#c8102e] pb-[env(safe-area-inset-bottom)]" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-white flex items-start justify-between p-5 border-b border-[#0a1f44]/10">
          <div>
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px] flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Patriots Announcement{visible.length > 1 ? 's' : ''}
            </div>
            <div id="announcement-popup-title" className="font-display text-[#0a1f44] text-2xl leading-tight mt-1">STAY IN THE LOOP</div>
          </div>
          <button onClick={dismissAll} data-testid="announcement-dismiss-all" aria-label="Dismiss all announcements" className="w-11 h-11 flex items-center justify-center text-[#0a1f44]/60 hover:text-[#c8102e] transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="divide-y divide-[#0a1f44]/10">
          {visible.map((a) => {
            const meta = TYPE_META[a.type] || TYPE_META.upcoming_event;
            const Icon = meta.icon;
            return (
              <div key={a.id} className="p-5" data-testid={`announcement-${a.id}`}>
                <AnnouncementMediaGallery announcementId={a.id} title={a.title} imageUrl={a.imageUrl} imageUrls={a.imageUrls} imageLayout={a.imageLayout} />
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center text-white shrink-0" style={{ backgroundColor: meta.color }}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading uppercase tracking-widest text-[9px]" style={{ color: meta.color }}>{meta.label}</div>
                    <div className="font-display text-[#0a1f44] text-lg leading-tight mt-1">{a.title}</div>
                    {(a.expiresAt || a.eventDate) && <div className="text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/55 mt-2" data-testid={`announcement-date-${a.id}`}>{formatAnnouncementExpiry(a)}</div>}
                    {a.body && <p className="text-sm text-[#0a1f44]/70 mt-2 whitespace-pre-line">{a.body}</p>}
                    {a.kickoffTime && <div className="mt-3"><KickoffCountdown id={`popup-${a.id}`} date={a.eventDate} time={a.kickoffTime} compact /></div>}
                    {a.id === PLAYOFF_ANNOUNCEMENT_ID && <PlayoffHypeCard id="popup" />}
                    <button onClick={() => dismiss(a.id)} className="mt-3 min-h-11 text-[10px] font-heading uppercase text-[#0a1f44]/50 hover:text-[#c8102e] transition-colors" data-testid={`announcement-dismiss-${a.id}`}>Dismiss</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="px-5 pb-5"><PopcornTeaser id="popup" /></div>
        <div className="sticky bottom-0 p-4 bg-white/95 backdrop-blur border-t border-[#0a1f44]/10 flex justify-end">
          <button onClick={dismissAll} data-testid="announcement-got-it" className="min-h-11 w-full sm:w-auto bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase text-[10px] px-5 py-2 transition-colors">Got It</button>
        </div>
      </div>
    </div>
  );
}
